import React, { useContext, useState, useEffect } from 'react';
import { MitWebsocket } from 'src/common/managers/MitWebsocket';
import { BroadcastMessageFormat, BroadcastReceiverContext } from 'src/states/BroadcastReceiverState';
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow } from "src/WindowManager/WindowState";
import { GlassButton } from './GlassButtons';
import { Localization, SessionContext } from '@viamap/viamap2-common';
import { MIBIcon } from 'src/componentsUtils/MIBIcon';

export function BroadcastReceiverDebug(props: { window: ManagedWindow, onClose: () => void }) {
   const { state: _broadcastReceiverState } = useContext(BroadcastReceiverContext);
   const { state: _sessionState } = useContext(SessionContext);
   const [apiStates, setApiStates] = useState<{ [key: string]: boolean }>({});
   const [showSuccessMessage, setShowSuccessMessage] = useState(false);

   const apis = [
      { text: 'Address API', feature: ['POI', 'BasicMap'], url: 'https://api.dataforsyningen.dk/rest/gsearch/v2.0/adresse', domain: 'Dataforsyningen' },
      { text: 'Login API', feature: 'POI', url: 'https://api.example.com/auth/login', domain: 'Login API' },
      { text: 'Data Processing API', feature: 'POI', url: 'https://api.example.com/data/process', domain: 'Data Processing API' },
      { text: 'EV Charger API', feature: 'POIEVCharger', url: 'https://api.example.com/ev/chargers', domain: 'EV Charger API' },
      { text: 'Search API', feature: 'SearchBar', url: 'https://api.example.com/search', domain: 'Search API' },
      { text: 'Map API', feature: 'BasicMap', url: 'https://api.example.com/map', domain: 'Map API' }
   ];

   const handleCheckboxChange = (url: string, checked: boolean) => {
      setApiStates(prev => ({
         ...prev,
         [url]: checked
      }));
   };

   const selectAll = () => {
      const allSelected = apis.reduce((acc, api) => {
         acc[api.url] = true;
         return acc;
      }, {} as { [key: string]: boolean });
      setApiStates(allSelected);
   };

   const deselectAll = () => {
      const allDeselected = apis.reduce((acc, api) => {
         acc[api.url] = false;
         return acc;
      }, {} as { [key: string]: boolean });
      setApiStates(allDeselected);
   };

   const toggleAll = () => {
      const allSelected = apis.every(api => apiStates[api.url]);
      if (allSelected) {
         deselectAll();
      } else {
         selectAll();
      }
   };

   const sendBroadcast = () => {
      const failedApis = apis.filter(api => !apiStates[api.url]);

      const message: BroadcastMessageFormat = {
         status: failedApis.length > 0 ? "error" : "success",
         affectedItems: failedApis.map(api => ({
            domain: api.domain,
            url: api.url,
            statusmessage: {
               da: `${api.text} er nede`,
               en: `${api.text} is down`
            },
            licenseFeature: api.feature
         }))
      };

      MitWebsocket.send({
         type: "sendToChannel",
         action: { channel: "broadcast", data: message }
      });

      // Show success message
      setShowSuccessMessage(true);
   };

   // debugState removed (unused)

   // Auto-hide success message after 3 seconds
   useEffect(() => {
      if (showSuccessMessage) {
         const timer = setTimeout(() => {
            setShowSuccessMessage(false);
         }, 3000);
         return () => clearTimeout(timer);
      }
   }, [showSuccessMessage]);

   // Clear error message when user starts typing

   // Check if text broadcast form is valid

   return (
      <AdvancedDragModal
         title={Localization.getText("Broadcast Debug Simulator")}
         window={props.window}
         onClose={props.onClose}
         PosDefault={{ left: "50%", top: "20%", width: "50%", height: "70%", transform: "translate(-50%, 0)" }}
      >
         <ADModalBody>
            <div className="broadcast-debug-content">
               {/* Tab Content */}
               <h4 className="broadcast-debug-section-title">{Localization.getText("API Status Simulation")}</h4>
               <p className="broadcast-debug-description">
                  {Localization.getText("Check the APIs that should succeed. Unchecked APIs will be marked as failed.")}
               </p>

               <div className="broadcast-debug-controls">
                  <GlassButton onClick={toggleAll} className="broadcast-debug-toggle-btn">
                     {Localization.getText("Toggle All")}
                  </GlassButton>
               </div>

               <div className="broadcast-debug-api-list">
                  {apis.map((api) => (
                     <div key={api.url} className="broadcast-debug-api-item">
                        <input
                           type="checkbox"
                           checked={apiStates[api.url] || false}
                           onChange={(e) => handleCheckboxChange(api.url, e.target.checked)}
                           className="broadcast-debug-api-checkbox"
                        />
                        <div className="broadcast-debug-api-content">
                           <div className="broadcast-debug-api-name">
                              {api.text}
                           </div>
                           <div className="broadcast-debug-api-domain">
                              {api.domain}
                           </div>
                           <div className="broadcast-debug-api-url">
                              {api.url}
                           </div>
                           <div className="broadcast-debug-api-feature">
                              {Localization.getText("Feature")}: {Array.isArray(api.feature) ? api.feature.join(', ') : api.feature}
                           </div>
                        </div>
                        <div className={`broadcast-debug-api-status ${apiStates[api.url] ? 'success' : 'fail'}`}>
                           {apiStates[api.url] ? Localization.getText('SUCCESS') : Localization.getText('FAIL')}
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </ADModalBody>
         <div style={{ height: '10px' }}></div>
         <ADModalFooter>
            {showSuccessMessage && (
               <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  color: '#4CAF50',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: showSuccessMessage ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
               }}>
                  <MIBIcon icon="allOk" />
                  <span>{Localization.getText("Message sent successfully!")}</span>
               </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
               <GlassButton onClick={sendBroadcast} className="broadcast-debug-send-btn">
                  {Localization.getText("Send Broadcast")}
               </GlassButton>
            </div>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}